import { useEffect, useRef, useState, useCallback } from "react";
import { format, subDays, addDays } from "date-fns";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS_KAMAR, STATUS_STYLE, statusLabel, JAMINAN, JENIS_KELAMIN_STYLE, JENIS_KELAMIN_ROW_BG } from "@/lib/constants";
import PasienForm from "@/components/PasienForm";
import DokumenBadges from "@/components/DokumenBadges";
import PasienDatangInfo from "@/components/PasienDatangInfo";
import Pagination from "@/components/Pagination";
import ClampedText from "@/components/ClampedText";
import CardField from "@/components/CardField";
import usePhone from "@/hooks/use-phone";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtDate, fmtDateTime, fmtSelisihWaktu } from "@/lib/format";
import { Plus, Search, Pencil, Ban, FileSpreadsheet, FileText } from "lucide-react";

// Admin IPRJT and Rawat Inap are read-only (view + export); mirrors PATIENT_WRITE_ROLES in backend/server.py.
const CAN_CREATE = ["super_admin"];
const CAN_EDIT_FULL = ["super_admin"];
const CAN_VOID = ["super_admin"];
const EXCEL_EXPORT_ROLES = ["super_admin", "admin_iprjt", "case_manager", "petugas_admisi"];   // Rawat Inap: PDF only
const HAS_ROW_ACTIONS = [...CAN_EDIT_FULL, ...CAN_VOID];   // Case Manager/Petugas Admisi update kamar via Antrian Kamar; Rawat Inap is read-only
// Diagnosa / Rencana Tindakan: for every role that receives them from GET /patients. Rawat Inap gets them through
// RAWAT_INAP_FIELDS since 13 Okt 2026 (the user's decision); it stays read-only (no Edit/Void/Tambah, PDF only).
const DX_TX_ROLES = ["super_admin", "admin_iprjt", "case_manager", "petugas_admisi", "rawat_inap"];
// Table area width from which Diagnosa and Rencana Tindakan get their own columns (1920px screen, sidebar open or
// collapsed, or about 1600px with it collapsed); narrower, they sit under the patient's name. The table scrolls sideways
// in both modes, as it always has. Below 640px the page shows cards instead.
const WIDE_TABLE_MIN = 1334;
// The on-screen table columns; the PDF export has exactly these (backend/tests/test_exports.py reads this list). The
// Diagnosa / Rencana Tindakan columns are screen-only, inserted after DPJP, so the PDF (which Rawat Inap also downloads)
// stays without them.
const BASE_COLUMNS = ["No. RM", "Nama Pasien", "Departemen", "DPJP", "Waktu Pengajuan", "Tanggal Masuk Rawat Inap", "Tgl Operasi", "LOS Pasien", "Jaminan", "Dokumen", "Status Kamar", "Kamar", "Pasien Datang", "Waktu Dapat Kamar", "Durasi Menunggu", "Lead Time Pengajuan"];

export default function PasienPage() {
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    search: "", basis: "tanggal_operasi",
    start: format(subDays(new Date(), 7), "yyyy-MM-dd"),
    end: format(addDays(new Date(), 14), "yyyy-MM-dd"),
    ksm_id: "", status_kamar: "", jaminan: "",
  });
  const [patients, setPatients] = useState([]);
  const [ksmList, setKsmList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidAlasan, setVoidAlasan] = useState("");
  const [voidLoading, setVoidLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [expanded, setExpanded] = useState(() => new Set());   // patient ids whose Diagnosa/Tindakan show in full
  const tableBoxRef = useRef(null);
  const [wide, setWide] = useState(true);
  const phone = usePhone();
  const showDxTx = DX_TX_ROLES.includes(user.role);

  // Re-attached when switching between cards and table, since the table box only exists in table mode.
  useEffect(() => {
    const el = tableBoxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) => setWide(entry.contentRect.width >= WIDE_TABLE_MIN));
    ro.observe(el);
    return () => ro.disconnect();
  }, [phone]);

  useEffect(() => {
    api.get("/master/ksm").then((r) => setKsmList(r.data)).catch(() => {});
  }, []);

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    try {
      const { data } = await api.get("/patients", { params });
      setPatients(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(fetchPatients, 250);
    return () => clearTimeout(t);
  }, [fetchPatients]);

  // Filters changed: recompute from the (new) filtered result, not wherever the user was paging before.
  useEffect(() => { setPage(1); }, [filters]);

  // A void/edit (or a page-size change) can leave `page` past the end of the now-current result; pull it back in.
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(patients.length / pageSize));
    setPage((p) => Math.min(p, totalPages));
  }, [patients.length, pageSize]);

  const pageItems = patients.slice((page - 1) * pageSize, page * pageSize);
  const dxTxColumns = showDxTx && wide;
  const screenColumns = dxTxColumns
    ? [...BASE_COLUMNS.slice(0, 4), "Diagnosa", "Rencana Tindakan", ...BASE_COLUMNS.slice(4)] : BASE_COLUMNS;
  const columns = HAS_ROW_ACTIONS.includes(user.role) ? [...screenColumns, "Aksi"] : screenColumns;

  const toggleExpanded = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (!next.delete(id)) next.add(id);
    return next;
  });

  const DX_TX = {
    diagnosa: { title: "Diagnosa", prefix: "Dx", testId: "diagnosa" },
    rencana_tindakan: { title: "Rencana Tindakan", prefix: "Tx", testId: "tindakan" },
  };
  // Same rendering as Antrian Kamar: two lines, tooltip, one "Lihat selengkapnya" per patient.
  const dxTx = (p, fields, withPrefix, className) => (
    <ClampedText expanded={expanded.has(p.id)} onToggle={() => toggleExpanded(p.id)} className={className}
      toggleTestId={`dxtx-toggle-${fields.length === 1 ? DX_TX[fields[0]].testId + "-" : ""}${p.no_rm}`}
      lines={fields.map((f) => ({ text: p[f], title: DX_TX[f].title, prefix: withPrefix ? DX_TX[f].prefix : undefined,
        testId: `${DX_TX[f].testId}-${p.no_rm}` }))} />
  );

  const jkBadge = (p) => (
    <span className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${JENIS_KELAMIN_STYLE[p.jenis_kelamin] || ""}`}
      data-testid={`jk-badge-${p.no_rm}`}>
      {p.jenis_kelamin === "Laki-laki" ? "Laki-laki" : "Perempuan"}
    </span>
  );

  const publikBadge = (p) => p.sumber === "pengajuan_publik" && (
    <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5"
      title={`Diajukan lewat form publik${p.nama_dokter ? ` oleh ${p.nama_dokter}` : ""}`}>
      <span className="whitespace-nowrap px-1.5 py-0.5 rounded border text-[10px] font-semibold bg-[#CDDC29]/20 text-[#6b7413] border-[#CDDC29]/50"
        data-testid={`badge-publik-${p.no_rm}`}>
        Pengajuan Publik
      </span>
      <span className="whitespace-nowrap font-mono text-[10px] text-slate-400">{p.kode_pengajuan}</span>
    </p>
  );

  const statusBadge = (p) => (
    <span className={`inline-block px-2.5 py-1 rounded-full border text-xs font-medium whitespace-nowrap ${STATUS_STYLE[p.status_kamar]}`}>
      {statusLabel(p.status_kamar)}
    </span>
  );

  const kamarText = (p) => p.kamar_rawat || (p.status_kamar === "Batal Operasi" ? "—" : "-");
  const durasiMenunggu = (p) => fmtSelisihWaktu(p.pasien_datang_at, p.waktu_dapat_kamar) || "-";
  const leadTime = (p) => fmtSelisihWaktu(p.created_at, p.waktu_dapat_kamar) || "-";

  const editButton = (p, className) => (
    <Button size="sm" variant="outline" className={`gap-1 text-xs ${className}`} onClick={() => openEdit(p)} data-testid={`edit-pasien-${p.no_rm}`}>
      <Pencil size={13} /> Edit
    </Button>
  );

  const voidButton = (p, className) => (
    <Button size="sm" variant="outline" className={`gap-1 text-xs text-red-600 border-red-200 hover:bg-red-50 ${className}`}
      onClick={() => { setVoidTarget(p); setVoidAlasan(""); }} data-testid={`void-pasien-${p.no_rm}`}>
      <Ban size={13} /> Void
    </Button>
  );

  const emptyText = `Tidak ada pasien pada filter ini.${user.role === "super_admin" && ksmList.length === 0 ? " Isi master data Departemen/DPJP terlebih dahulu." : ""}`;

  const openEdit = (p) => {
    setEditTarget(p);
    setFormOpen(true);
  };

  const handleVoid = async () => {
    if (!voidAlasan.trim()) {
      toast.error("Alasan void wajib diisi");
      return;
    }
    setVoidLoading(true);
    try {
      await api.post(`/patients/${voidTarget.id}/void`, { alasan: voidAlasan });
      toast.success("Data pasien berhasil di-void");
      setVoidTarget(null);
      setVoidAlasan("");
      fetchPatients();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setVoidLoading(false);
    }
  };

  const handleExport = async (fmt) => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const res = await api.get("/patients/export", { params: { ...params, format: fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fmt === "pdf" ? "jadwal_pasien.pdf" : "jadwal_pasien.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Export ${fmt.toUpperCase()} berhasil diunduh`);
    } catch (e) {
      toast.error("Gagal mengunduh file export");
    }
  };

  return (
    <div className="space-y-5" data-testid="pasien-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Jadwal Pasien</h1>
          <p className="text-sm text-slate-500 mt-1">
            {patients.length} pasien ditemukan.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {EXCEL_EXPORT_ROLES.includes(user.role) && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => handleExport("xlsx")} data-testid="export-excel-button">
              <FileSpreadsheet size={14} /> Excel
            </Button>
          )}
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => handleExport("pdf")} data-testid="export-pdf-button">
            <FileText size={14} /> PDF
          </Button>
          {CAN_CREATE.includes(user.role) && (
            <Button size="sm" className="gap-1.5 bg-[#00B9AD] hover:bg-[#009E94] text-white"
              onClick={() => { setEditTarget(null); setFormOpen(true); }}
              data-testid="add-patient-button">
              <Plus size={14} /> Tambah Pasien
            </Button>
          )}
        </div>
      </div>

      <Card className="border-slate-200/80 shadow-sm">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3 items-end">
            <div className="col-span-2">
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Cari Nama / No. RM</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input className="pl-8" placeholder="Ketik nama atau No. RM..."
                  value={filters.search} data-testid="pasien-search-input"
                  onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Basis Tanggal</label>
              <Select value={filters.basis} onValueChange={(v) => setFilters((f) => ({ ...f, basis: v }))}>
                <SelectTrigger data-testid="pasien-basis-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="tanggal_operasi">Tgl Operasi</SelectItem>
                  <SelectItem value="tanggal_input">Waktu Pengajuan</SelectItem>
                  <SelectItem value="tanggal_masuk_rawat_inap">Tgl Masuk Ranap</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Dari</label>
              <Input type="date" value={filters.start} data-testid="pasien-filter-start"
                onChange={(e) => setFilters((f) => ({ ...f, start: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Sampai</label>
              <Input type="date" value={filters.end} data-testid="pasien-filter-end"
                onChange={(e) => setFilters((f) => ({ ...f, end: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Departemen</label>
              <Select value={filters.ksm_id || "semua"} onValueChange={(v) => setFilters((f) => ({ ...f, ksm_id: v === "semua" ? "" : v }))}>
                <SelectTrigger data-testid="pasien-filter-ksm"><SelectValue placeholder="Semua" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="semua">Semua Departemen</SelectItem>
                  {ksmList.map((k) => <SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Status Kamar</label>
              <Select value={filters.status_kamar || "semua"} onValueChange={(v) => setFilters((f) => ({ ...f, status_kamar: v === "semua" ? "" : v }))}>
                <SelectTrigger data-testid="pasien-filter-status"><SelectValue placeholder="Semua" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="semua">Semua Status</SelectItem>
                  {STATUS_KAMAR.map((s) => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {phone ? (
        // Phone: one card per patient with every table column (and the same actions), nothing scrolls sideways.
        <div className="space-y-3" data-testid="pasien-cards">
          {loading ? (
            <Card className="border-slate-200/80 shadow-sm"><CardContent className="py-14 text-center text-sm text-slate-400">Memuat data...</CardContent></Card>
          ) : patients.length === 0 ? (
            <Card className="border-slate-200/80 shadow-sm"><CardContent className="py-14 text-center text-sm text-slate-400">{emptyText}</CardContent></Card>
          ) : pageItems.map((p) => (
            <article key={p.id} className="rounded-xl border border-slate-200/80 bg-white shadow-sm overflow-hidden" data-testid={`pasien-row-${p.no_rm}`}>
              <div className={`p-3.5 space-y-3 ${JENIS_KELAMIN_ROW_BG[p.jenis_kelamin] || ""}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800 break-words">{p.nama_pasien}</p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-slate-500">
                      <span className="font-mono text-slate-600">{p.no_rm}</span>
                      <span aria-hidden="true">·</span>
                      <span className="whitespace-nowrap">{p.umur} th</span>
                      {jkBadge(p)}
                    </p>
                    {publikBadge(p)}
                  </div>
                  <div className="shrink-0">{statusBadge(p)}</div>
                </div>
                {showDxTx && dxTx(p, ["diagnosa", "rencana_tindakan"], true)}
                <dl className="grid grid-cols-2 gap-x-3 gap-y-2.5 border-t border-slate-900/10 pt-3">
                  <CardField label="Departemen">{p.ksm_nama || "-"}</CardField>
                  <CardField label="DPJP">{p.dpjp_nama || "-"}</CardField>
                  <CardField label="Waktu Pengajuan"><span data-testid={`waktu-pengajuan-${p.no_rm}`}>{fmtDateTime(p.created_at)}</span></CardField>
                  <CardField label="Tgl Masuk Rawat Inap">
                    <span data-testid={`tanggal-masuk-ranap-${p.no_rm}`}>{p.tanggal_masuk_rawat_inap ? fmtDate(p.tanggal_masuk_rawat_inap) : "-"}</span>
                  </CardField>
                  <CardField label="Tgl Operasi">{fmtDate(p.tanggal_operasi)}</CardField>
                  <CardField label="LOS Pasien"><span data-testid={`los-pasien-${p.no_rm}`}>{p.los_pasien || "-"}</span></CardField>
                  <CardField label="Jaminan">{p.jaminan}{p.kelas_jaminan ? ` (Kelas ${p.kelas_jaminan})` : ""}</CardField>
                  <CardField label="Kamar">{kamarText(p)}</CardField>
                  <CardField label="Dokumen"><DokumenBadges patient={p} /></CardField>
                  <CardField label="Pasien Datang"><div data-testid={`pasien-datang-${p.no_rm}`}><PasienDatangInfo patient={p} wrap /></div></CardField>
                  <CardField label="Waktu Dapat Kamar">
                    <span data-testid={`waktu-dapat-kamar-${p.no_rm}`}>{p.waktu_dapat_kamar ? fmtDateTime(p.waktu_dapat_kamar) : "-"}</span>
                  </CardField>
                  <CardField label="Durasi Menunggu"><span className="font-medium" data-testid={`durasi-menunggu-${p.no_rm}`}>{durasiMenunggu(p)}</span></CardField>
                  <CardField label="Lead Time Pengajuan" className="col-span-2"><span data-testid={`lead-time-${p.no_rm}`}>{leadTime(p)}</span></CardField>
                </dl>
                {HAS_ROW_ACTIONS.includes(user.role) && (
                  <div className="flex gap-2 border-t border-slate-900/10 pt-3">
                    {CAN_EDIT_FULL.includes(user.role) && editButton(p, "h-10 flex-1 bg-white")}
                    {CAN_VOID.includes(user.role) && voidButton(p, "h-10 flex-1 bg-white")}
                  </div>
                )}
              </div>
            </article>
          ))}
          {!loading && patients.length > 0 && (
            <Card className="border-slate-200/80 shadow-sm overflow-hidden">
              <Pagination page={page} pageSize={pageSize} total={patients.length}
                onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
                itemLabel="pasien" testId="pasien-pagination" />
            </Card>
          )}
        </div>
      ) : (
      <Card className="border-slate-200/80 shadow-sm overflow-hidden">
        <div className="overflow-x-auto" ref={tableBoxRef} data-layout={wide ? "wide" : "compact"}>
          <table className="w-full text-sm min-w-[2010px]" data-testid="pasien-table">
            <thead className="bg-[#267B84] text-white">
              <tr>
                {columns.map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {loading ? (
                <tr><td colSpan={columns.length} className="px-4 py-14 text-center text-slate-400">Memuat data...</td></tr>
              ) : patients.length === 0 ? (
                <tr><td colSpan={columns.length} className="px-4 py-14 text-center text-slate-400">{emptyText}</td></tr>
              ) : pageItems.map((p) => (
                <tr key={p.id} className={`transition-colors ${JENIS_KELAMIN_ROW_BG[p.jenis_kelamin] || "hover:bg-slate-50"}`} data-testid={`pasien-row-${p.no_rm}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600 whitespace-nowrap">{p.no_rm}</td>
                  <td className="px-4 py-3 min-w-[150px]">
                    <p className="font-medium text-slate-800">{p.nama_pasien}</p>
                    <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-400">
                      {p.umur} th
                      {jkBadge(p)}
                    </p>
                    {publikBadge(p)}
                    {showDxTx && !wide && dxTx(p, ["diagnosa", "rencana_tindakan"], true, "mt-1.5 max-w-[200px]")}
                  </td>
                  <td className="px-4 py-3 text-slate-600 min-w-[130px]">{p.ksm_nama}</td>
                  <td className="px-4 py-3 text-slate-600 min-w-[130px]">{p.dpjp_nama}</td>
                  {dxTxColumns && (
                    <>
                      <td className="px-4 py-3 min-w-[150px]">{dxTx(p, ["diagnosa"], false, "max-w-[220px]")}</td>
                      <td className="px-4 py-3 min-w-[150px]">{dxTx(p, ["rencana_tindakan"], false, "max-w-[220px]")}</td>
                    </>
                  )}
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap" data-testid={`waktu-pengajuan-${p.no_rm}`}>
                    {fmtDateTime(p.created_at)}
                  </td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap" data-testid={`tanggal-masuk-ranap-${p.no_rm}`}>
                    {p.tanggal_masuk_rawat_inap ? fmtDate(p.tanggal_masuk_rawat_inap) : "-"}
                  </td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(p.tanggal_operasi)}</td>
                  <td className="px-4 py-3 text-slate-600 min-w-[110px]" data-testid={`los-pasien-${p.no_rm}`}>{p.los_pasien || "-"}</td>
                  <td className="px-4 py-3 text-slate-600">{p.jaminan}{p.kelas_jaminan ? ` (Kelas ${p.kelas_jaminan})` : ""}</td>
                  <td className="px-4 py-3"><DokumenBadges patient={p} /></td>
                  <td className="px-4 py-3">{statusBadge(p)}</td>
                  <td className="px-4 py-3 text-slate-600">{kamarText(p)}</td>
                  <td className="px-4 py-3" data-testid={`pasien-datang-${p.no_rm}`}><PasienDatangInfo patient={p} /></td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap" data-testid={`waktu-dapat-kamar-${p.no_rm}`}>
                    {p.waktu_dapat_kamar ? fmtDateTime(p.waktu_dapat_kamar) : "-"}
                  </td>
                  {/* Durasi Menunggu: pasien datang -> dapat kamar (the main metric). Lead Time: pengajuan -> dapat kamar (report only). */}
                  <td className="px-4 py-3 text-slate-700 font-medium whitespace-nowrap" data-testid={`durasi-menunggu-${p.no_rm}`}>
                    {durasiMenunggu(p)}
                  </td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap" data-testid={`lead-time-${p.no_rm}`}>
                    {leadTime(p)}
                  </td>
                  {HAS_ROW_ACTIONS.includes(user.role) && <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      {CAN_EDIT_FULL.includes(user.role) && editButton(p, "h-8")}
                      {CAN_VOID.includes(user.role) && voidButton(p, "h-8")}
                    </div>
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && patients.length > 0 && (
          <Pagination page={page} pageSize={pageSize} total={patients.length}
            onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            itemLabel="pasien" testId="pasien-pagination" />
        )}
      </Card>
      )}

      <PasienForm
        open={formOpen}
        onOpenChange={setFormOpen}
        patient={editTarget}
        mode="full"
        onSaved={fetchPatients}
      />

      <Dialog open={Boolean(voidTarget)} onOpenChange={(v) => !v && setVoidTarget(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="void-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Void Data Pasien</DialogTitle>
            <DialogDescription className="text-xs">
              {voidTarget?.nama_pasien} ({voidTarget?.no_rm}) — data tidak dihapus permanen, hanya ditandai void untuk audit trail.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea rows={3} placeholder="Alasan void (wajib)..." value={voidAlasan}
              onChange={(e) => setVoidAlasan(e.target.value)} data-testid="void-alasan-input" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setVoidTarget(null)} data-testid="void-cancel-button">Batal</Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white" disabled={voidLoading}
                onClick={handleVoid} data-testid="void-confirm-button">
                {voidLoading ? "Memproses..." : "Void Data"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
